import BUILD from 'config/build.json';
import ENV from 'config/env.conf';
import { DEPLOYMENT, NODE, REACT, V } from 'constants/env.const';
import { nowAsIso } from 'utils/date/now.util';
import { flagB, flagI, flagS } from 'utils/flag.util';

const CTAG = '[CONF]';
const CTAG2 = '[INIT-RELATED]';

const logW = /**@type VariadicV*/ console.warn.bind(console, '[W]', CTAG, CTAG2); // eslint-disable-line no-console,no-unused-vars
const logI = /**@type VariadicV*/ console.info.bind(console, '[I]', CTAG, CTAG2); // eslint-disable-line no-console,no-unused-vars
const logD = /**@type VariadicV*/ console.log.bind(console, '[D]', CTAG, CTAG2); // eslint-disable-line no-console,no-unused-vars

const PUSHER_ID_STAGE = '11ea0d95bbfdba6c003f';
const PUSHER_ID_PROD = '3f8b9d67092fe086e4dc';

const URL_STRIPE_REDIRECT_STAGE =
  'https://connect.stripe.com/express/oauth/authorize?redirect_uri=https://staging-core.api.getslick.com/billing/authorize/stripe/signup/&client_id=ca_H0OGkVc3buSWnxMYDsNEv814eaxsTOqu';
const URL_STRIPE_REDIRECT_PROD =
  'https://connect.stripe.com/express/oauth/authorize?redirect_uri=https://core.api.getslick.com/billing/authorize/stripe/signup/&client_id=ca_H0OG8NF1LCv32DBkU1SDrfUXm8T3qNVw';

const URL_ROOT_BOOK_STAGE = 'https://staging-book.getslick.com/#/salon/';
const URL_ROOT_BOOK_PROD = 'https://book.getslick.com/#/salon/';

const URL_NCOB_V2_STAGE = 'https://v2-staging-book.getslick.com/salon';
const URL_NCOB_V2_PROD = 'https://v2-book.getslick.com/salon';

const LINK_DASHBOARD_BIGMAILER_STAGE = 'https://app.bigmailer.io';
const LINK_DASHBOARD_BIGMAILER_PROD = 'https://email.getslick.com';

const URL_SALON_STAGE = 'https://staging-salon.getslick.com/salon/';
const URL_SALON_QA = 'https://qa-salon.getslick.com/salon/';
const URL_SALON_PROD = 'https://salon.getslick.com';

const URL_MARKETING_PROD = 'https://getslick.com';
const URL_MARKETING_STAGE = 'https://staging-www.getslick.com';

const DOMAIN_BOOK_STAGE = 'staging-book.getslick.com';
const DOMAIN_BOOK_PROD = 'book.getslick.com';

const API_ROOT_CORE_PROD = 'https://core.api.getslick.com/';
const API_ROOT_CORE_STAGE = 'https://staging-core.api.getslick.com/';

const API_ROOT_TX_PROD = 'https://transaction.api.getslick.com/';
const API_ROOT_TX_STAGE = 'https://staging-transaction.api.getslick.com/';

const API_ROOT_STOCK_PROD = 'https://stock.api.getslick.com/';
const API_ROOT_STOCK_STAGE = 'https://staging-stock.api.getslick.com/';

const API_ROOT_ACT_PROD = 'https://activity.api.getslick.com/';
const API_ROOT_ACT_STAGE = 'https://staging-activity.api.getslick.com/';

const API_ROOT_PHONE_PROD = 'https://phone.api.getslick.com/';
const API_ROOT_PHONE_STAGE = 'https://staging-phone.api.getslick.com/';

const CONF_COGNITO_NONE = Object.freeze({ region: '', userPool: '', identityPool: '', clientId: '' });
const CONF_COGNITO_TEST = Object.freeze({
  region: 'eu-west-1',
  userPool: 'eu-west-1_test',
  identityPool: 'test',
  clientId: 'clientIdTest',
});
const CONF_COGNITO_STAGE = Object.freeze({
  region: 'eu-west-1',
  userPool: 'eu-west-1_7kPv2hrlw',
  identityPool: 'eu-west-1:a0b30661-7428-4cfa-a944-d85537683204',
  clientId: '3mciqme7tl73r8ho5m0qj1rflu',
});
const CONF_COGNITO_PROD = Object.freeze({
  region: 'eu-west-1',
  userPool: 'eu-west-1_Dzo9vjrfZ',
  identityPool: 'eu-west-1:fdff45a1-7a65-4eac-ac75-55e3fae0d461',
  clientId: '10o4v9c0ti02cav9jljkf5ilu9',
});

// default values
const defaultConfiguration = () =>
  Object.freeze({
    //
    environment: DEPLOYMENT.dev, //
    //
    apiRoot: '', //
    transactionApiRoot: '', //
    stockApiRoot: '', //
    activityApiRoot: '', //
    phoneApiRoot: '', //
    //
    sentryDsn: '', //
    //
    segmentWriteKey: '', //
    //
    cognitoConfig: CONF_COGNITO_NONE, //
    //
    marketingSite: URL_MARKETING_PROD, //
    bigMailerDashboardUrl: '',
    salonUrl: '', //
    bookingUrlRoot: '', //
    onlineBookingUrl: '', //
    ncobV2BaseUrl: '', //
    //
    pusherId: '', //
    //
    stripeKey: '', //
    stripeRedirectUrl: '', //
  });

const nodeTest = () =>
  Object.freeze({
    //
    environment: DEPLOYMENT.test, //
    //
    apiRoot: flagS('http://127.0.0.1:8000/', ENV.REACT_APP_API_ROOT_CORE), //
    transactionApiRoot: flagS('http://127.0.0.1:8003/', ENV.REACT_APP_API_ROOT_TX), //
    //
    sentryDsn: flagS('', ENV.REACT_APP_SENTRY_DSN), //
    sentryEnable: flagB(false, ENV.REACT_APP_SENTRY_ENABLE), //
    sentryVersion: flagI(1, ENV.REACT_APP_SENTRY_VERSION), //
    //
    cognitoConfig: CONF_COGNITO_TEST, //
    //
    salonUrl: URL_SALON_STAGE, //
    bookingUrlRoot: URL_ROOT_BOOK_STAGE, //
    onlineBookingUrl: DOMAIN_BOOK_STAGE, //
    //
    stripeRedirectUrl: URL_STRIPE_REDIRECT_STAGE, //
  });

const nodeDev = () =>
  Object.freeze({
    //
    environment: DEPLOYMENT.dev, //
    //
    apiRoot: flagS(API_ROOT_CORE_STAGE, ENV.REACT_APP_API_ROOT_CORE), //
    transactionApiRoot: flagS(API_ROOT_TX_STAGE, ENV.REACT_APP_API_ROOT_TX), //
    stockApiRoot: flagS(API_ROOT_STOCK_STAGE, ENV.REACT_APP_API_ROOT_STOCK), //
    activityApiRoot: flagS(API_ROOT_ACT_STAGE, ENV.REACT_APP_API_ROOT_ACT), //
    phoneApiRoot: flagS(API_ROOT_PHONE_STAGE, ENV.REACT_APP_API_ROOT_PHONE), //
    //
    sentryDsn: flagS('', ENV.REACT_APP_SENTRY_DSN), //
    sentryEnable: flagB(false, ENV.REACT_APP_SENTRY_ENABLE), //
    sentryVersion: flagI(1, ENV.REACT_APP_SENTRY_VERSION), //
    //
    segmentWriteKey: flagS('', ENV.REACT_APP_SEGMENT_WRITE_KEY), //
    //
    cognitoConfig: CONF_COGNITO_STAGE, //
    //
    marketingSite: URL_MARKETING_STAGE, //
    salonUrl: URL_SALON_STAGE, //
    bookingUrlRoot: URL_ROOT_BOOK_STAGE, //
    onlineBookingUrl: DOMAIN_BOOK_PROD, //
    ncobV2BaseUrl: URL_NCOB_V2_STAGE, //
    //
    pusherId: PUSHER_ID_STAGE, //
    //
    stripeKey: 'pk_test_FIuLXvpFwPME72VBVeFGmN9L', //
    stripeRedirectUrl: URL_STRIPE_REDIRECT_STAGE, //
    bigMailerDashboardUrl: LINK_DASHBOARD_BIGMAILER_STAGE, //
  });

const nodeProd = () =>
  Object.freeze({
    //
    sentryDsn: flagS('https://bf6ce8a549754c539f3e9ddb4802870c@sentry.io/299752', ENV.REACT_APP_SENTRY_DSN), //
    sentryEnable: flagB(true, ENV.REACT_APP_SENTRY_ENABLE), //
    sentryVersion: flagI(1, ENV.REACT_APP_SENTRY_VERSION), //
    //
    salonUrl: URL_SALON_PROD, //
    onlineBookingUrl: DOMAIN_BOOK_PROD, //
    ncobV2BaseUrl: URL_NCOB_V2_PROD, //
    //
    stripeKey: 'pk_live_tPL8nW2Jjh0dJqnAOso8Qpn0', //
    bigMailerDashboardUrl: LINK_DASHBOARD_BIGMAILER_PROD, //
  });

const reactStage = () =>
  Object.freeze({
    ...nodeProd(), //
    //
    environment: DEPLOYMENT.staging, //
    //
    apiRoot: flagS(API_ROOT_CORE_STAGE, ENV.REACT_APP_API_ROOT_CORE), //
    transactionApiRoot: flagS(API_ROOT_TX_STAGE, ENV.REACT_APP_API_ROOT_TX), //
    stockApiRoot: flagS(API_ROOT_STOCK_STAGE, ENV.REACT_APP_API_ROOT_STOCK), //
    activityApiRoot: flagS(API_ROOT_ACT_STAGE, ENV.REACT_APP_API_ROOT_ACT), //
    phoneApiRoot: flagS(API_ROOT_PHONE_STAGE, ENV.REACT_APP_API_ROOT_PHONE), //
    //
    segmentWriteKey: flagS('rdQHCFAWPtgsJF9bIXxUJ4Bn7IokhWvp', ENV.REACT_APP_SEGMENT_WRITE_KEY), //
    //
    cognitoConfig: CONF_COGNITO_STAGE, //
    //
    marketingSite: URL_MARKETING_STAGE, //
    salonUrl: URL_SALON_STAGE, //
    onlineBookingUrl: DOMAIN_BOOK_STAGE, //
    bookingUrlRoot: URL_ROOT_BOOK_STAGE, //
    ncobV2BaseUrl: URL_NCOB_V2_STAGE, //
    //
    pusherId: PUSHER_ID_STAGE, //
    //
    stripeKey: 'pk_test_FIuLXvpFwPME72VBVeFGmN9L', //
    stripeRedirectUrl: URL_STRIPE_REDIRECT_STAGE, //
    bigMailerDashboardUrl: LINK_DASHBOARD_BIGMAILER_STAGE, //
  });

const reactQA = () =>
  Object.freeze({
    ...nodeProd(), //
    //
    environment: DEPLOYMENT.qa, //
    //
    apiRoot: flagS(API_ROOT_CORE_STAGE, ENV.REACT_APP_API_ROOT_CORE), //
    transactionApiRoot: flagS(API_ROOT_TX_STAGE, ENV.REACT_APP_API_ROOT_TX), //
    stockApiRoot: flagS(API_ROOT_STOCK_STAGE, ENV.REACT_APP_API_ROOT_STOCK), //
    activityApiRoot: flagS(API_ROOT_ACT_STAGE, ENV.REACT_APP_API_ROOT_ACT), //
    phoneApiRoot: flagS(API_ROOT_PHONE_STAGE, ENV.REACT_APP_API_ROOT_PHONE), //
    //
    segmentWriteKey: flagS('rdQHCFAWPtgsJF9bIXxUJ4Bn7IokhWvp', ENV.REACT_APP_SEGMENT_WRITE_KEY), //
    //
    cognitoConfig: CONF_COGNITO_STAGE, //
    //
    marketingSite: URL_MARKETING_STAGE, //
    salonUrl: URL_SALON_QA, //
    onlineBookingUrl: DOMAIN_BOOK_STAGE, //
    bookingUrlRoot: URL_ROOT_BOOK_STAGE, //
    ncobV2BaseUrl: URL_NCOB_V2_STAGE, //
    //
    pusherId: PUSHER_ID_STAGE, //
    //
    stripeKey: 'pk_test_FIuLXvpFwPME72VBVeFGmN9L', //
    stripeRedirectUrl: URL_STRIPE_REDIRECT_STAGE, //
    bigMailerDashboardUrl: LINK_DASHBOARD_BIGMAILER_STAGE, //
  });

const reactLab = () =>
  Object.freeze({
    ...reactStage(), //
    //
    environment: DEPLOYMENT.lab, //
    //
    // NOTE: lab deploy uses staging backend roots (same as staging/qa)
  });

const reactBeta = () =>
  Object.freeze({
    ...nodeProd(), //
    environment: ENV.REACT_APP_ENV, //
    //
    apiRoot: flagS(API_ROOT_CORE_PROD, ENV.REACT_APP_API_ROOT_CORE), //
    transactionApiRoot: flagS(API_ROOT_TX_PROD, ENV.REACT_APP_API_ROOT_TX), //
    stockApiRoot: flagS(API_ROOT_STOCK_PROD, ENV.REACT_APP_API_ROOT_STOCK), //
    activityApiRoot: flagS(API_ROOT_ACT_PROD, ENV.REACT_APP_API_ROOT_ACT), //
    phoneApiRoot: flagS(API_ROOT_PHONE_PROD, ENV.REACT_APP_API_ROOT_PHONE), //
    //
    segmentWriteKey: flagS('Cki7eUA5FGTxROfInYOFf97FxBv4DdC3', ENV.REACT_APP_SEGMENT_WRITE_KEY), //
    //
    cognitoConfig: CONF_COGNITO_PROD, //
    //
    salonUrl: URL_SALON_PROD, //
    bookingUrlRoot: URL_ROOT_BOOK_PROD, //
    onlineBookingUrl: DOMAIN_BOOK_PROD, //
    ncobV2BaseUrl: URL_NCOB_V2_PROD, //
    //
    pusherId: PUSHER_ID_PROD, //
    //
    stripeKey: 'pk_live_tPL8nW2Jjh0dJqnAOso8Qpn0', //
    stripeRedirectUrl: URL_STRIPE_REDIRECT_PROD, //
    bigMailerDashboardUrl: LINK_DASHBOARD_BIGMAILER_PROD, //
  });

const reactMaster = () =>
  Object.freeze({
    ...nodeProd(), //
    environment: DEPLOYMENT.prod, //
    //
    apiRoot: flagS(API_ROOT_CORE_PROD, ENV.REACT_APP_API_ROOT_CORE), //
    transactionApiRoot: flagS(API_ROOT_TX_PROD, ENV.REACT_APP_API_ROOT_TX), //
    stockApiRoot: flagS(API_ROOT_STOCK_PROD, ENV.REACT_APP_API_ROOT_STOCK), //
    activityApiRoot: flagS(API_ROOT_ACT_PROD, ENV.REACT_APP_API_ROOT_ACT), //
    phoneApiRoot: flagS(API_ROOT_PHONE_PROD, ENV.REACT_APP_API_ROOT_PHONE), //
    //
    segmentWriteKey: flagS('Cki7eUA5FGTxROfInYOFf97FxBv4DdC3', ENV.REACT_APP_SEGMENT_WRITE_KEY), //
    //
    cognitoConfig: CONF_COGNITO_PROD,
    onlineBookingUrl: DOMAIN_BOOK_PROD, //
    salonUrl: URL_SALON_PROD, //
    bookingUrlRoot: URL_ROOT_BOOK_PROD, //
    ncobV2BaseUrl: URL_NCOB_V2_PROD, //
    pusherId: PUSHER_ID_PROD, //
    stripeRedirectUrl: URL_STRIPE_REDIRECT_PROD, //
    bigMailerDashboardUrl: LINK_DASHBOARD_BIGMAILER_PROD, //
  });

const construct = () => {
  const FTAG = 'construct()';

  const D = defaultConfiguration();
  logD(FTAG, 'D:', D);

  const B = Object.freeze({
    //
    buildCiBranch: flagS('', NODE.dev === ENV.NODE_ENV ? 'development' : BUILD.CI_BRANCH),
    buildCiNum: flagS('', NODE.dev === ENV.NODE_ENV ? '1234567890' : BUILD.CI_NUM),
    buildCiTag: flagS('', NODE.dev === ENV.NODE_ENV ? 'development' : BUILD.CI_TAG),
    buildNow: flagS('', NODE.dev === ENV.NODE_ENV ? nowAsIso() : BUILD.NOW),
    buildShaLong: flagS('', NODE.dev === ENV.NODE_ENV ? 'abcdef7890123456789012345678901234abcdef' : BUILD.SHA_LONG),
    buildShaShort: flagS('', NODE.dev === ENV.NODE_ENV ? 'abcdef' : BUILD.SHA_SHORT), //
    //
    experiments: flagB(true, ENV.REACT_APP_USE_EXPERIMENTS),
    reduxDevtoolsSerialize: flagB(false, ENV.REACT_APP_REDUX_DEVTOOLS_SERIALIZE),
    registerServiceWorker: flagB(true, ENV.REACT_APP_REGISTER_SERVICE_WORKER), //
    //
    upscopeUse: flagB(true, ENV.REACT_APP_UPSCOPE_USE), //
  });
  logD(FTAG, 'B:', B, '<-', BUILD);

  const reactEnv = ENV.REACT_APP_ENV ? ENV.REACT_APP_ENV : REACT.stage;
  logD(FTAG, 'R:', reactEnv, '<-', ENV.REACT_APP_ENV);

  if (NODE.dev === ENV.NODE_ENV) return { v: V.d, ...D, ...nodeDev(), ...B, reactEnv, nodeEnv: NODE.dev };
  if (NODE.test === ENV.NODE_ENV) return { v: V.t, ...D, ...nodeTest(), ...B, reactEnv, nodeEnv: NODE.test };

  if (NODE.prod !== ENV.NODE_ENV) logW(FTAG, 'Unknown process.env.NODE_ENV', ENV.NODE_ENV);
  if (NODE.prod !== ENV.NODE_ENV) return { v: V.p, ...D, ...B, reactEnv, nodeEnv: ENV.NODE_ENV };

  // NOTE: all that follows is PROD on the Node side and only differs on the React side
  if (ENV.REACT_APP_ENV === REACT.stage) return { v: V.s, ...D, ...reactStage(), ...B, reactEnv, nodeEnv: NODE.prod };
  if (ENV.REACT_APP_ENV === REACT.qa) return { v: V.s, ...D, ...reactQA(), ...B, reactEnv, nodeEnv: NODE.prod };
  if (ENV.REACT_APP_ENV === REACT.lab) return { v: V.s, ...D, ...reactLab(), ...B, reactEnv, nodeEnv: NODE.prod };
  if (ENV.REACT_APP_ENV === REACT.beta) return { v: V.b, ...D, ...reactBeta(), ...B, reactEnv, nodeEnv: NODE.prod };
  if (ENV.REACT_APP_ENV === REACT.alpha) return { v: V.b, ...D, ...reactBeta(), ...B, reactEnv, nodeEnv: NODE.prod };

  return { v: V.m, ...D, ...reactMaster(), ...B, reactEnv, nodeEnv: NODE.prod };
};

logI('BUILD:', BUILD);

const CONF = construct();
Object.freeze(CONF);

logI('CONF:', CONF);
logI('V:', JSON.stringify({ v: CONF.v, e: CONF.environment, n: CONF.nodeEnv, r: CONF.reactEnv }));

export default CONF;
